import { Injectable } from '@angular/core';
import { FilterService } from '../../../interfaces/filter/filter-service';
import { tap } from 'rxjs/operators';
import { MaterialItemsQuery } from './material-items.query';
import { MaterialItemsStore } from './material-items.store';
import { TableColumn } from '../../../models/table-column';
import { Observable } from 'rxjs';
import { MaterialBackendService } from '../../../services/material/material-backend.service';
import { TaskResultGeneric } from '../../../models/task-result-generic';
import { LocalStateKeys } from '../../local-state-keys';
import { OrderItemsStoreService } from '../../order-items-store-service';
import { MaterialModel } from '../../../models/material/material-model';
import { MaterialQuery } from '../../../models/material/material-query.model';
import { LocalStoreConfigModel } from '../../../models/local-store-config.model';
import { ISavedListSettingsService } from '../../../interfaces/settings/saved-list-settings.service';
import { TaskResult } from 'src/app/core/models/client-models';

@Injectable({
  providedIn: 'root',
})
export class MaterialItemsService implements FilterService, ISavedListSettingsService {
  constructor(
    private materialItemsQuery: MaterialItemsQuery,
    private orderItemsStoreService: OrderItemsStoreService,
    private materialItemsStore: MaterialItemsStore,
    private materialBackendService: MaterialBackendService
  ) {}

  setFilterOptions(filterSelection: any, storeConfig?: LocalStoreConfigModel): void {
    this.materialItemsStore.update((state) => ({
      ...state,
      filterOptions: filterSelection,
    }));
  }

  /**
   * Select next cost center list page.
   */
  getNextPage(orderStoreKey: LocalStateKeys) {
    this.materialItemsStore.setLoading(true);
    const orderItemsQuery =
      this.orderItemsStoreService.getOrCreateStore(orderStoreKey).query;
    // Get values for http params.
    const nextPage: number = this.materialItemsQuery.getValue().page + 1;
    this.setPage(nextPage);
    const pageSize: number = this.materialItemsQuery.getValue().pageSize;

    let materialParams = {
      pageNumber: nextPage,
      pageSize,
      orderID: orderItemsQuery.getActive()?.orderID,
      filterFullText: true,
      full: false,
    } as MaterialQuery;

    materialParams = this.setFilter(materialParams);
    materialParams = this.setSorting(materialParams);

    this.materialBackendService
      .getMaterialList(materialParams)
      .pipe(tap(() => this.materialItemsStore.setLoading(false)))
      .subscribe({
        next: (data) => {
          this.materialItemsStore.upsertMany(data.resultObject.items);
          this.materialItemsStore.setError(null);
          if (data.resultObject.items.length === 0) {
            // if no results set page to previous.
            this.setPage(nextPage - 1);
            // set last page indicator.
            this.materialItemsStore.update((state) => (state.lastPage = true));
          }
        },
        error: (error) => this.materialItemsStore.setError(error.error),
      });
  }

  setSorting(params: MaterialQuery) {
    const sortKey = this.materialItemsQuery.getValue()?.sortKey;
    const sortDirection = this.materialItemsQuery.getValue()?.sortDirection;

    params.sortBy = sortKey;
    params.sortOrder = sortDirection;

    return params;
  }

  setFilter(params: MaterialQuery) {
    const values = this.materialItemsQuery.getValue()?.filterOptions;
    const filterValue = this.materialItemsQuery.getValue()?.filterValue;

    const filterAll =
      !values.note.selected &&
      !values.number.selected &&
      !values.name.selected &&
      !values.description.selected &&
      !values.quantity.selected &&
      !values.unit.selected &&
      !values.price.selected &&
      !values.personNR.selected &&
      !values.lastName.selected &&
      !values.firstName.selected;

    // eslint-disable-next-line id-blacklist
    params.number = values.number.selected || filterAll ? filterValue : '';
    params.note = values.number.selected || filterAll ? filterValue : '';
    params.description = values.name.selected || filterAll ? filterValue : '';
    params.description2 =
      values.description.selected || filterAll ? filterValue : '';

    if (Number(filterValue)) {
      params.quantityFrom =
        values.quantity.selected || filterAll ? Number(filterValue) : null;
      params.quantityTo =
        values.unit.selected || filterAll ? Number(filterValue) : null;
      params.priceTo =
        values.price.selected || filterAll ? Number(filterValue) : null;
      params.priceFrom =
        values.price.selected || filterAll ? Number(filterValue) : null;
    }

    params.personalNumber =
      values.personNR.selected || filterAll ? filterValue : '';

    params.firstName =
      values.firstName.selected || filterAll ? filterValue : '';
    params.lastName = values.lastName.selected || filterAll ? filterValue : '';

    return params;
  }

  setPage(newPage: number) {
    this.materialItemsStore.update((state) => ({
      ...state,
      page: newPage,
    }));
  }

  clearCache() {
    this.materialItemsStore.update((state) => ({
      ...state,
      page: 0,
      currentPage: 0,
      pageSize: 50,
      lastPage: false,
    }));
    this.materialItemsStore.remove();
  }

  clearFilter() {
    this.materialItemsStore.update((state) => ({
      ...state,
      filterValue: '',
    }));
  }

  /**
   * Set new Selected Columns.
   *
   * @param columns New selection of columns.
   */
  showSelectedColumns(columns: TableColumn[]): void {
    this.materialItemsStore.update((state) => ({
      ...state,
      columns,
    }));
  }

  filterValueChange(filterValue: string, orderStoreKey: LocalStateKeys) {
    this.clearCache();
    this.materialItemsStore.update((state) => ({
      ...state,
      filterValue,
    }));
    this.getNextPage(orderStoreKey);
  }

  onSort(
    sortKey: string,
    sortDirection: string,
    orderItemsKey: LocalStateKeys
  ) {
    this.clearCache();
    if (sortDirection === '') {
      sortKey = '';
    }

    if (sortKey.indexOf('.') !== -1) {
      sortKey = sortKey.split('.')[1];
      if (sortKey.split('.')[0] === 'person' && sortKey === 'number') {
        sortKey = 'personalNumber';
      }
    }

    this.materialItemsStore.update((state) => ({
      ...state,
      sortDirection,
      sortKey,
    }));
    this.getNextPage(orderItemsKey);
  }

  setActive(row: MaterialModel) {
    this.materialItemsStore.setActive(row?.id);
  }

  saveChangedMaterial(changedMaterial: MaterialModel): Observable<TaskResult> {
    this.materialItemsStore.setLoading(true);
    return this.materialBackendService.saveChangedMaterial(changedMaterial);
  }

  saveNewMaterial(
    newMaterial: MaterialModel
  ): Observable<TaskResultGeneric<MaterialModel>> {
    this.materialItemsStore.setLoading(true);
    return this.materialBackendService.saveNewMaterial(newMaterial);
  }

  deleteMaterial(
    material: MaterialModel
  ): Observable<TaskResultGeneric<MaterialModel>> {
    this.materialItemsStore.setLoading(true);
    return this.materialBackendService.deleteMaterial(material);
  }
}
